import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  TrackByFunction,
  ViewChildren,
} from '@angular/core';
import { AsyncValidatorFn } from '@angular/forms';
import { merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { IntersectionMarkerDirective } from '../../directives/intersection-marker/intersection-marker.directive';
import { Single } from '../../utils';
import { HeroViewModel } from '../hero/hero.view-model';
import { AbstractPageScreenService } from './abstract-page-screen.service';
import { PageItemToDisplayType, PageItemViewModel, PageItemViewModelToDisplay, PageScreenViewModel } from './page-screen.view-model';

@Component({
  selector: 'osn-shared-page-screen',
  templateUrl: './page-screen.component.html',
  styleUrls: ['./page-screen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageScreenComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren(IntersectionMarkerDirective) public intersectionBoundary: QueryList<IntersectionMarkerDirective>;
  @ViewChildren('pageItem') public pageItemElements: QueryList<ElementRef>;
  public vm$: Observable<PageScreenViewModel> = this.service.vm$;
  public observer = new IntersectionObserver((entries) => this.intersect(entries), { rootMargin: `50%`, threshold: [0, 1] });
  public trackbyFn: TrackByFunction<PageItemViewModel> = (_, x) => x.slug;
  public asyncPrimaryCredentialValidator: AsyncValidatorFn = this.service.asyncPrimaryCredentialValidator;

  public PageItemToDisplayType = PageItemToDisplayType;
  public pageItemsWithEmailBlocker$: Observable<PageItemViewModelToDisplay[]> = this.vm$.pipe(
    map((vm) => {
      const emailBlockerIndex = vm.pageItems.findIndex((pageItem) => pageItem.type === 'MarketingEmailBlocker');

      // if there's no email blocker on the page, we just map it to the right format
      if (emailBlockerIndex === -1) {
        return vm.pageItems.map((pageItem) => ({ type: PageItemToDisplayType.Simple, pageItem }));
      }

      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const firstEmailBlockerUnderlayItemIndex = emailBlockerIndex - 2;
      const emailBlockerUnderlayItems = [vm.pageItems[firstEmailBlockerUnderlayItemIndex], vm.pageItems[emailBlockerIndex - 1]];
      const extraMinHeightMultiplier = emailBlockerUnderlayItems.reduce((acc, curr) => {
        if (curr.type === 'CollectionCarousel' || curr.type === 'SeasonCollectionCarousel' || curr.type === 'Hero') {
          acc += 1;
        }
        return acc;
      }, 0);
      return [
        ...vm.pageItems
          .slice(undefined, firstEmailBlockerUnderlayItemIndex)
          .map((pageItem) => ({ type: PageItemToDisplayType.Simple, pageItem } as const)),
        {
          type: PageItemToDisplayType.EmailBlockerContainer,
          items: emailBlockerUnderlayItems,
          // if the two carousel before the email collector has any kind of collection trays
          // we need to add a 50vw height extra to be able to show the email blocker high enough
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          minHeight: extraMinHeightMultiplier > 0 ? `${extraMinHeightMultiplier * 50}vw` : undefined,
          emailBlockerItem: vm.pageItems[emailBlockerIndex],
        },
        ...vm.pageItems.slice(emailBlockerIndex + 1).map((pageItem) => ({ type: PageItemToDisplayType.Simple, pageItem } as const)),
      ];
    })
  );
  public firstItemTopMargin$ = this.pageItemsWithEmailBlocker$.pipe(
    map((items) => {
      let itemToCheck;
      if (items[0].type === PageItemToDisplayType.Simple) {
        itemToCheck = items[0].pageItem;
      } else {
        itemToCheck = items[0].items[0];
      }

      return itemToCheck.type !== 'Hero' && itemToCheck.type !== 'MarketingEmailCollector' && itemToCheck.type !== 'SpotlightViewModel';
    })
  );

  private readonly destroyed$ = new Subject<void>();
  private readonly intersections$ = new Subject<number>();
  private readonly heroItems$ = this.vm$.pipe(
    map((vm) => vm.pageItems.filter((pageItem) => pageItem.type === 'Hero')[0]),
    filter((heroPageItem) => !!(heroPageItem as HeroViewModel)?.items.length),
    map((heroPageItem) => (heroPageItem as HeroViewModel)?.items),
    distinctUntilChanged((a, b) => a.length === b.length)
  );

  constructor(private readonly service: AbstractPageScreenService) {}

  public ngOnInit(): void {
    // TODO: This solution is not ideal, but it works as a temporary workaround
    this.intersections$.pipe(takeUntil(this.destroyed$)).subscribe((dataIndex) => {
      for (let i = 0; i <= dataIndex; i++) {
        this.service.resolvePageItems(i);
      }
    });
  }

  public ngAfterViewInit(): void {
    const existingChildren$ = new Single<IntersectionMarkerDirective[]>();
    existingChildren$.next(this.intersectionBoundary.toArray());

    const changes$ = this.intersectionBoundary.changes.pipe(map(() => this.intersectionBoundary.toArray()));

    // If the page was once loaded already, the contents of the intersection boundary would not change AFTER view init,
    // making this merge necessary
    merge(existingChildren$, changes$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((items) => {
        this.observer.disconnect();
        items.forEach((item) => this.observer.observe(item.element.nativeElement));
      });

    this.heroItems$.pipe(take(1), takeUntil(this.destroyed$)).subscribe((heroItems) => this.service.measurePromotionImpression(heroItems));
  }

  public ngOnDestroy(): void {
    this.service.removeHrefLinkTags();
    this.service.removeCanonicalTag();
    this.service.unloadPage();
    this.observer.disconnect();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onCarouselItemSelect(contentInfo: { guid: string; carouselTitle: string; carouselItemType: string }): void {
    this.service.measureProductClick(contentInfo.guid, contentInfo.carouselTitle, contentInfo.carouselItemType);
  }

  public onCarouselItemImpression(contentInfo: { guid: string; index: number; carouselTitle: string; carouselItemType: string }): void {
    this.service.measureProductImpression(contentInfo.guid, contentInfo.index, contentInfo.carouselTitle, contentInfo.carouselItemType);
  }

  public onHeroItemButtonClick({ actionType, videoLength, seriesTitle, heroItem, index, buttonText }): void {
    this.service.measureHeroItemActionClick({ actionType, videoLength, seriesTitle, heroItem, buttonText });
    this.service.measurePromotionClick(heroItem, index);
  }

  public onHeroItemNavigationByTheUser({ heroItem, videoLength }): void {
    this.service.measureHeroItemNavigationByTheUser(heroItem, videoLength);
  }

  public onHeroItemGenreClick({ heroItem, genreName, videoLength }): void {
    this.service.measureHeroGenreClick(heroItem, genreName, videoLength);
  }

  public onMarketingDeviceModuleButtonClick(buttonText: string): void {
    this.service.measureStartFreeTrialClick('device module start free trial', buttonText);
  }

  public onEmailBlockerButtonClick(buttonText: string): void {
    this.service.measureStartFreeTrialClick('email blocker start free trial', buttonText);
  }

  public onEmailSubmitted(email: string): void {
    this.service.signUp(email);
  }

  private intersect(entries: IntersectionObserverEntry[]) {
    entries.forEach(({ isIntersecting, target }) => {
      if (isIntersecting) {
        this.intersections$.next(Number(target.getAttribute('data-index')));
      }
    });
  }
}

export * from './abstract-page-screen.service';
export * from './page-screen-mapper.service';
